/* tslint:disable:forbid-import */
import { autorun, computed } from 'mobx';
/* tslint:enable:forbid-import */
import { INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM } from 'owa-inline-image-consts';
import { trace } from 'owa-trace';
import type PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import type EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import type { EditorViewState, PluginWithViewState, PluginViewStateAdapter } from 'owa-editor';
import type InlineImageViewState from '../schema/InlineImageViewState';
import {
    AttachmentWellViewState,
    CreateAttachmentsProperties,
    InlineAttachmentStatus,
    lazySetInlineAttachmentStatus,
} from 'owa-attachment-well-data';
import {
    lazyClearInlineImageStore,
    lazyLoadFromAttachment,
    lazyLoadFromProxy,
} from 'owa-inline-image-loader';
import {
    AttachmentFile,
    AttachmentFileList,
    ExpirableBase64InlineImageFile,
    createLocalComputerFiles,
    LocalComputerFile,
    AttachmentFileType,
} from 'owa-attachment-file-types';
import {
    cleanupImageCopyHandler,
    handleImageCopy,
} from 'owa-inline-image-loader/lib/utils/ImageCopyHandler';
import { BeforePasteEvent, IEditor, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import {
    INSERT_PLACEHOLDER_CHANGE_SOURCE,
    PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME,
    INSERT_INLINEIMAGE_CHANGE_SOURCE,
    PASTED_IMAGE_NAME_PREFIX,
    PASTED_IMAGE_NAME_SUFFIX,
} from '../utils/constants';
import deleteImageState from '../actions/deleteImageState';
import processInlineImageAfterUploadSuccess from '../utils/processInlineImageAfterUploadSuccess';
import onImageError from '../utils/onImageError';
import type PastedBase64InlineImageFile from '../schema/PastedBase64InlineImageFile';
import convertAllContentIdToAttachmentUrl from '../utils/convertAllContentIdToAttachmentUrl';

const PASTED_EXPIRABLE_IMAGE_NAME_PREFIX = 'pastedExpirableImage';

// The image proxy endpoint attribute
const ATTRIBUTE_IMAGEPROXYENDPOINT = 'data-imageproxyendpoint';

// The content change source for addins
const ADDIN_CONTENT_CHANGE_SOURCE = 'Addins';

// The content change source for SetContent
const SETCONTENT_CONTENT_CHANGE_SOURCE = 'SetContent';

// The content change source for upgrading compose
const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';

// The context used for inline image load (load from existing content, draft, forward etc.)
const CONTEXT_COMPOSE = 'Compose';

// Attribute name for expirable image.
const EXPIRABLE_IMAGE_ATTR_NAME = 'data-willexpire';

// Paste image
const SAFARI_IMAGE_BLOB_PREFIX = 'blob:';
const WEBKIT_FAKE_URL_IMAGE_PREFIX = 'webkit-fake-url:';
const LOCAL_FILE_REFERENCE_REGEX = new RegExp('^file:///[a-zA-Z/]', 'i');
const BASE64_IMAGE_REGEX = new RegExp('^data:(image\\/\\w+);base64,([A-Za-z0-9+/=]+?)$', 'i');

const INLINE_ATTACHMENT_PROP: CreateAttachmentsProperties = {
    isInline: true,
    isHiddenInline: false,
    shouldShare: false,
};

const ALLOWED_ATTRIBUTES_WHEN_PASTE = ['originalsrc', 'crossorigin', 'contenttype'];

/*
 * Notes: all potential calling orders are listed below.
 * These functions in the class are called asyncly:
 * A: insertPlaceholderImage called on get image dimension callback, in the error case it return dimensions as 0 * 0
 * B: processInlineImageAfterUploadSuccess called from createattachment response success
 * B': onImageError called from createattachment response failure (C/C' will not happen in this case)
 * C: onImageLoad called from actual image onload
 * C': onImageError called from actual image onerror
 * C/C' is bound to happen after B, but the order of A and B/C is uncertain
 *
 * So we have potential orders as below (Please refer to InlineImagePluginTest for expected results):
 * 1: A, B, C (placeholder was inserted before createattachment succeeded)
 * 2: A, B' (placeholder was inserted before createattachment failed)
 * 3: A, B, C' (placeholder was inserted before createattachment succeeded, but actual image failed to load)
 *
 * 4. B, C, A (createattachment succeeded before placeholder was inserted)
 * 5. B, C', A (createattachment succeeded before placeholder was inserted, but actual image failed to load)
 * 6. B', A (createattachment failed before placeholder was inserted)
 *
 * 7. B, A, C (createattachment succeeded before placeholder was inserted, but actual image was loaded after)
 * 8. B, A, C' (createattachment succeeded before placeholder was inserted, but actual image was loaded after and failed)
 *
 * Also if A happens before B/C, placeholder can be removed by user, call it -A.
 * Note that -A can not happen after B', C, C'.
 * So additional potential orders are
 * 9. A, -A, B, C/C' (placeholder was inserted then deleted by user before createattachment succeeded, then actual image was loaded/failed to load)
 * 10. A, -A, B' (placeholder was inserted then deleted by user before createattachment failed)
 * 11. A, B, -A, C/C' (placeholder was inserted before createattachment succeeded, then deleted by user before actual image was loaded/failed to load)
 * 12. B, A, -A, C/C' (createattachment succeeded, then placeholder was inserted and deleted by user before actual image was loaded/failed to load)
 */

export default class InlineImagePlugin
    implements PluginWithViewState<InlineImageViewState>, EditorPluginEx {
    private editor: IEditor;
    private pluginUtils: PluginUtils;
    private autorunDisposer: any;
    private onCopyDisposer: () => void;
    private observerDisposer: () => void;

    constructor(
        public readonly adapter: PluginViewStateAdapter<InlineImageViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private readonly attachmentAdapter: PluginViewStateAdapter<AttachmentWellViewState>,
        private readonly createAttachments: (
            files: AttachmentFileList,
            createAttachmentsProperties: CreateAttachmentsProperties
        ) => void,
        private readonly deleteInlineAttachments: (placeholderId: string) => void,
        private readonly isInlineImageDiabled?: boolean,
        private readonly userIdentity?: string
    ) {
        // InlineImageStore keys dataURIs by originalsrc. Since originalsrc is only guaranteed to be unique within a message,
        // clear the store to avoid any false positives.
        lazyClearInlineImageStore.importAndExecute();
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;
        const inlineAttachments = this.attachmentAdapter().inlineAttachments;

        const inlineAttachmentStatusComputed = computed(() => {
            return inlineAttachments.map(inlineAttachment => inlineAttachment.status);
        });

        this.onCopyDisposer = this.editor.addDomEventHandler('copy', this.onCopy);

        this.autorunDisposer = autorun(() => {
            const computed = inlineAttachmentStatusComputed.get();
            computed.forEach((status, index) => {
                const attachment = inlineAttachments[index];
                if (attachment.isHiddenInline) {
                    return;
                }
                if (
                    status == InlineAttachmentStatus.CreateAttachmentSucceeded ||
                    status == InlineAttachmentStatus.CreateAttachmentFailed ||
                    status == InlineAttachmentStatus.ConvertAttachmentInlineSucceeded
                ) {
                    if (
                        status == InlineAttachmentStatus.CreateAttachmentSucceeded ||
                        status == InlineAttachmentStatus.ConvertAttachmentInlineSucceeded
                    ) {
                        if (!attachment.skipProcessInlineImage) {
                            processInlineImageAfterUploadSuccess(
                                this.adapter(),
                                this.editorAdapter(),
                                attachment.attachmentId,
                                attachment.placeholderId
                            );
                        }
                    } else {
                        onImageError(
                            this.adapter(),
                            this.editorAdapter(),
                            attachment.placeholderId
                        );
                    }

                    // Complete the inline attachment.
                    lazySetInlineAttachmentStatus.importAndExecute(
                        attachment,
                        InlineAttachmentStatus.Completed
                    );
                }
            });
        });
    }

    public dispose(): void {
        this.editor = null;
        this.stopObserver(true /*force*/);
        this.autorunDisposer();
        cleanupImageCopyHandler();
        if (this.onCopyDisposer) {
            this.onCopyDisposer();
            this.onCopyDisposer = null;
        }
    }

    public getName() {
        return 'InlineImage';
    }

    public onPluginEvent(event: PluginEvent): void {
        switch (event.eventType) {
            case PluginEventType.BeforePaste:
                this.processInlineImage(event);
                break;
            case PluginEventType.ContentChanged:
                switch (event.source) {
                    case ADDIN_CONTENT_CHANGE_SOURCE:
                        convertAllContentIdToAttachmentUrl(this.editor, this.attachmentAdapter());
                        break;

                    case SETCONTENT_CONTENT_CHANGE_SOURCE:
                    case UPGRADE_CHANGE_SOURCE:
                        this.loadImages();
                        break;
                    case INSERT_PLACEHOLDER_CHANGE_SOURCE:
                        // Start observing on the parent for the placeholder node. This is so that we will be notified when the node is removed
                        // so we can cleanup the pending upload. Note: according to the documentaion for MutationObserver calling .observe multiple
                        // times on with same element has no effect.
                        trace.info('InlineImagePlugin: Start observing on content editable div');
                        this.observerDisposer = this.pluginUtils.observeMutation(
                            this.processNodeDeleted,
                            {
                                subtree: true,
                                childList: true,
                            }
                        );
                        break;

                    case INSERT_INLINEIMAGE_CHANGE_SOURCE:
                        this.stopObserver();
                        break;
                }
                break;
            case PluginEventType.EditorReady:
                this.loadImages();
                break;
        }
    }

    public setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    private onCopy = (event: UIEvent) => {
        handleImageCopy(event.currentTarget as HTMLDivElement);
    };

    private processInlineImage(event: BeforePasteEvent) {
        if (!event.clipboardData.image || event.clipboardData.text) {
            if (event.fragment.querySelectorAll('span[fluid-url], div[fluid-url]').length > 0) {
                return;
            }
            const images = event.fragment.querySelectorAll('img');
            const imageCount = images.length;
            const base64InlineImageFiles: AttachmentFile[] = [];
            let base64FileIndex = 0;
            let expireableImageIndex = 0;
            for (let i = 0; i < imageCount; i++) {
                const image = images[i];
                const imageWillExpire = image.getAttribute(EXPIRABLE_IMAGE_ATTR_NAME);
                const imageSrc = image.getAttribute('src');
                // Check for images with no src or images pasted from Safari which don't have usable sources
                if (
                    !imageSrc ||
                    imageSrc.indexOf(SAFARI_IMAGE_BLOB_PREFIX) === 0 ||
                    imageSrc.indexOf(WEBKIT_FAKE_URL_IMAGE_PREFIX) === 0 ||
                    imageSrc.match(LOCAL_FILE_REFERENCE_REGEX)
                ) {
                    // Remove all images with no src or local file src
                    image.parentNode.removeChild(image);
                    continue;
                }

                if (imageWillExpire && imageWillExpire.toLowerCase() == 'true') {
                    const expirableImageFileInfo: ExpirableBase64InlineImageFile = {
                        fileType: AttachmentFileType.ExpirableBase64InlineImage,
                        name:
                            PASTED_EXPIRABLE_IMAGE_NAME_PREFIX +
                            'base64' +
                            expireableImageIndex +
                            PASTED_IMAGE_NAME_SUFFIX,
                        size: 0,
                        type: '',
                        dataUri: '',
                        tempId:
                            PASTED_EXPIRABLE_IMAGE_NAME_PREFIX +
                            Date.now() +
                            '-' +
                            expireableImageIndex,
                        image,
                        imageSrc: imageSrc,
                    };
                    image.src = '';
                    base64InlineImageFiles.push(expirableImageFileInfo);
                    expireableImageIndex++;
                } else {
                    // Check for images with src as base64 data uri
                    const base64Match = imageSrc.match(BASE64_IMAGE_REGEX);
                    if (base64Match && base64Match.length === 3) {
                        const base64InlineImageFile: PastedBase64InlineImageFile = {
                            name:
                                PASTED_IMAGE_NAME_PREFIX +
                                'base64' +
                                base64FileIndex++ +
                                PASTED_IMAGE_NAME_SUFFIX,
                            size: 0, // default to 0 since we cannot get size of image with src as data uri.
                            type: base64Match[1],
                            dataUri: base64Match[2],
                            imageNode: image,
                            fileType: AttachmentFileType.Base64InlineImage,
                        };
                        base64InlineImageFiles.push(base64InlineImageFile);
                    }
                }
            }
            if (base64InlineImageFiles.length > 0) {
                if (this.isInlineImageDiabled) {
                    while (event.fragment.firstChild) {
                        event.fragment.removeChild(event.fragment.firstChild);
                    }
                } else {
                    this.createAttachments(base64InlineImageFiles, INLINE_ATTACHMENT_PROP);
                }
            }
        } else {
            while (event.fragment.firstChild) {
                event.fragment.removeChild(event.fragment.firstChild);
            }

            if (!this.isInlineImageDiabled) {
                this.createAttachmentsForLocalFiles(event.clipboardData.image);
            }
        }

        const allowedAttributes = event.sanitizingOption.additionalAllowedAttributes;
        allowedAttributes.push.apply(allowedAttributes, ALLOWED_ATTRIBUTES_WHEN_PASTE);
    }

    private createAttachmentsForLocalFiles(file: File) {
        const localComputerFile: LocalComputerFile = createLocalComputerFiles([file])[0];
        if (!localComputerFile.name) {
            localComputerFile.name = PASTED_IMAGE_NAME_PREFIX + 'file' + PASTED_IMAGE_NAME_SUFFIX;
        }
        this.createAttachments([localComputerFile], INLINE_ATTACHMENT_PROP);
    }

    private stopObserver(force?: boolean) {
        // If there aren't any pending inline attachments then we don't need the mutation observer to
        // listen for anything. It will start observing again if a new placeholder is added
        if (
            this.observerDisposer &&
            (force || Object.keys(this.adapter().imageStatusMap).length === 0)
        ) {
            trace.info('InlineImagePlugin: Disconnect mutation observer');
            this.observerDisposer();
            this.observerDisposer = null;
        }
    }

    // Process the mutation record looking for placeholder images that have been deleted
    private processNodeDeleted = (nodes: MutationRecord[]) => {
        for (let i = 0; i < nodes.length; i++) {
            const removedNodes = nodes[i].removedNodes;
            if (removedNodes) {
                for (let j = 0; j < removedNodes.length; j++) {
                    const removedNode = removedNodes.item(j);

                    // Check the removed node to see if it is a placeholder that is still pending load
                    const temporaryIdForRemovedNode = this.getPendingPlaceholderForNode(
                        removedNode
                    );
                    if (temporaryIdForRemovedNode) {
                        trace.info(
                            'InlineImagePlugin: Pending image placeholder removed from DOM. Deleting the attachment.'
                        );
                        this.deleteInlineAttachments(temporaryIdForRemovedNode);
                        deleteImageState(this.adapter(), temporaryIdForRemovedNode);
                    }
                }
            }
        }

        this.stopObserver();
    };

    // Checks to see if the given node is a pending placeholder, and it if is return its tempid.
    // Otherwise recursively check all of its children to find a pending placholder.
    private getPendingPlaceholderForNode(node: Node): string {
        if (
            node.nodeName.toLowerCase() == 'div' &&
            (<HTMLElement>node).hasAttribute('tempid') &&
            (<HTMLElement>node).hasAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME)
        ) {
            return (<HTMLElement>node).getAttribute('tempid');
        } else if (node.hasChildNodes()) {
            for (let i = 0; i < node.childNodes.length; i++) {
                const tempid = this.getPendingPlaceholderForNode(node.childNodes[i]);
                if (tempid) {
                    return tempid;
                }
            }
        }

        return null;
    }

    private async loadImages() {
        const loadFromAttachment = await lazyLoadFromAttachment.import();
        const loadFromProxy = await lazyLoadFromProxy.import();

        this.editor?.queryElements<HTMLImageElement>(
            `img[${INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM}]`,
            image =>
                loadFromAttachment(
                    image,
                    CONTEXT_COMPOSE,
                    false /* usePlaceHolder */,
                    this.userIdentity
                )
        );

        this.editor?.queryElements<HTMLImageElement>(
            `img[${ATTRIBUTE_IMAGEPROXYENDPOINT}]`,
            image => loadFromProxy(image)
        );
    }
}
